import { sortBy } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { useParams } from 'react-router-dom';
import { StepComponent } from './types';
import { Form, validationSchema } from './form-controller';
import useAsyncFetch from '../../../hooks/use-async';
import {
  AtividadesService,
  ParametroService,
  PeriodosService,
  PropriedadesService,
  EmpreendimentosService,
} from '../../../services';
import useLocalStore from '../../../hooks/use-local-store';
import useRecomendacaoSemeaduraStore from './store';

const Parametros: StepComponent = ({
  formObject,
  recomendacaoSemeadura,
  safras,
}) => {
  const currentPropriedade = useLocalStore('currentPropriedade').value;
  const fetchCultivares = useRecomendacaoSemeaduraStore(
    (state) => state.fetchCultivares,
  );
  const { watch, resetField, setValue } = formObject.form;
  const params = useParams<{ id: string }>();

  const [
    sementeCobertura,
    clienteAgrogestao,
    talhoes,
    propriedadeId,
    safraId,
    atividadeId,
    situacao,
  ] = watch([
    'sementeCobertura',
    'clienteAgrogestao',
    'talhoes',
    'propriedade.id',
    'safra.id',
    'atividade.id',
    'situacao',
  ]);

  const hasTalhoesSelecionados = useMemo(() => talhoes.length > 0, [talhoes]);

  const canEditSafraPeriodo = useMemo(() => situacao === 'aberto', [situacao]);

  const { data: parametro } = useAsyncFetch(
    {
      callback: () => ParametroService.parametro(),
      errorMessage: 'Falha ao carregar os parâmetros.',
      withLoader: true,
    },
    [],
  );

  const { data: propriedades } = useAsyncFetch(
    {
      callback: async () =>
        sortBy(await PropriedadesService.list(), 'descricao'),
      errorMessage: 'Falha ao carregar propriedades.',
      withLoader: true,
    },
    [],
  );

  const { data: atividades } = useAsyncFetch(
    {
      callback: () => AtividadesService.list(),
      errorMessage: 'Falha ao carregar atividades.',
      withLoader: true,
    },
    [],
  );

  const { data: periodos } = useAsyncFetch(
    {
      callback: () => PeriodosService.list(),
      errorMessage: 'Falha ao carregar períodos.',
      withLoader: true,
    },
    [],
  );

  useEffect(() => {
    if (sementeCobertura) {
      resetField('espacamento');
      resetField('porcentagemAreaRemonte');
      resetField('porcentagemCorrecaoDensidade');
      resetField('porcentagemCorrecaoQuantidade');
      resetField('distribuicaoRegulagemSemente');
      resetField('distribuicaoRegulagemAdubo');
      resetField('comMargemSeguranca');
    }
  }, [sementeCobertura, resetField]);

  const { data: empreendimentos } = useAsyncFetch(
    {
      errorMessage: 'Falha ao carregar empreendimentos.',
      withLoader: true,
      callback: async () => {
        if (!clienteAgrogestao || !atividadeId || !safraId || !propriedadeId) {
          return;
        }

        return EmpreendimentosService.list({
          atividadeId,
          safraId,
          propriedadeId,
        });
      },
    },
    [safraId, atividadeId, propriedadeId, clienteAgrogestao],
  );

  const periodosFiltered = useMemo(() => {
    if (!periodos?.length) return [];
    if (!clienteAgrogestao) return periodos;
    if (!empreendimentos) return [];

    const periodosFilteredIds = empreendimentos.map(
      (empreendimento) => empreendimento.cdPeriodo,
    );

    return periodos.filter((periodo) =>
      periodosFilteredIds.includes(periodo.codigo),
    );
  }, [clienteAgrogestao, periodos, empreendimentos]);

  useEffect(() => {
    setValue('clienteAgrogestao', !parametro?.naoClienteAgrogestao || false, {
      shouldValidate: true,
    });
  }, [parametro?.naoClienteAgrogestao, setValue]);

  useEffect(() => {
    const currentSafra = safras?.find((safra) => safra.current);

    if (currentSafra) setValue('safra', currentSafra);
  }, [safras, setValue]);

  useEffect(() => {
    if (!recomendacaoSemeadura) return;

    const safra = safras?.find(
      ({ id }) => recomendacaoSemeadura.safraId === id,
    );

    setValue('safra', safra, { shouldValidate: Boolean(safra) });
  }, [safras, recomendacaoSemeadura, setValue]);

  useEffect(() => {
    if (!recomendacaoSemeadura) return;

    const periodo = periodos?.find(
      ({ codigo }) => recomendacaoSemeadura.periodo?.codigo === codigo,
    );

    setValue('periodo', periodo, { shouldValidate: Boolean(periodo) });
  }, [periodos, recomendacaoSemeadura, setValue]);

  useEffect(() => {
    if (!recomendacaoSemeadura) return;

    const atividade = atividades?.find(
      ({ id }) => recomendacaoSemeadura.atividade.id === id,
    );

    setValue('atividade', atividade, { shouldValidate: Boolean(atividade) });
  }, [atividades, recomendacaoSemeadura, setValue]);

  useEffect(() => {
    const novaPropriedadeId =
      recomendacaoSemeadura?.propriedade.id || currentPropriedade?.id;

    if (!propriedades || !novaPropriedadeId) return;

    const propriedade = propriedades?.find(
      ({ id }) => novaPropriedadeId === id,
    );
    setValue('propriedade', propriedade, {
      shouldValidate: Boolean(propriedade),
    });

    if (!propriedade) return;
    fetchCultivares(propriedade.id);
  }, [
    currentPropriedade,
    propriedades,
    setValue,
    recomendacaoSemeadura,
    fetchCultivares,
  ]);

  return (
    <div className="formgrid grid">
      <div className="field col-12 md:pb-2 flex align-items-center">
        <Form.Checkbox
          label="Semeadura de Cobertura"
          name="sementeCobertura"
          containerClassName="inline-block mb-0 normal-font-size"
          disabled={!!params.id || hasTalhoesSelecionados}
        />
        <i
          id="sementeCoberturaInfo"
          className="pi pi-info-circle ml-2"
          data-pr-tooltip="Quando marcada a opção Semeadura de Cobertura a recomendação considera a dose/ha e não a densidade de sementes por ha. Não é possível alterar na edição."
        />
        <Tooltip
          target="#sementeCoberturaInfo"
          className="semente-cobertura-info-tooltip"
        />
      </div>

      <Form.Dropdown
        options={propriedades}
        dataKey="id"
        optionLabel="descricao"
        label="Propriedade:"
        name="propriedade"
        containerClassName="field col-6 md:col-4 mb-0 mt-0"
        disabled={hasTalhoesSelecionados}
      />
      <Form.Dropdown
        options={atividades}
        dataKey="id"
        optionLabel="descricao"
        label="Atividade:"
        name="atividade"
        containerClassName="field col-6 md:col-4 mb-0"
        disabled={hasTalhoesSelecionados}
      />
      <Form.Dropdown
        options={safras}
        dataKey="id"
        optionLabel="descricao"
        label="Safra:"
        name="safra"
        containerClassName="field col-6 mb-0 md:col-2"
        disabled={!canEditSafraPeriodo}
      />
      <Form.Dropdown
        options={periodosFiltered}
        dataKey="codigo"
        optionLabel="descricao"
        label="Período:"
        name="periodo"
        containerClassName="field col-6 md:col-2 mb-0"
        disabled={!canEditSafraPeriodo}
      />
      <Form.InputNumber
        label="Rendimento operacional mínimo:"
        name="rendimentoOperacionalMinimo"
        addonRight="ha/dia"
        containerClassName="field col-6 md:col-4 mb-0"
        dependencies={['rendimentoOperacionalMaximo']}
      />
      <Form.InputNumber
        label="Rendimento operacional máximo:"
        name="rendimentoOperacionalMaximo"
        addonRight="ha/dia"
        containerClassName="field col-6 md:col-4 mb-0"
      />
      <Form.InputNumber
        label="Espaçamento:"
        name="espacamento"
        addonRight="m"
        containerClassName="field col-6 md:col-4 mb-0"
        disabled={sementeCobertura}
      />
      <Form.InputNumber
        label="Área de remonte:"
        name="porcentagemAreaRemonte"
        addonRight="%"
        containerClassName="field col-6 md:col-4 mb-0"
        disabled={sementeCobertura}
      />
      <Form.InputNumber
        label="Correção de densidade (remonte):"
        name="porcentagemCorrecaoDensidade"
        addonRight="%"
        containerClassName="field col-6 md:col-4 mb-0"
        disabled={sementeCobertura}
      />
      <Form.InputNumber
        label="Correção de quantidade (umidade do solo):"
        name="porcentagemCorrecaoQuantidade"
        addonRight="%"
        containerClassName="field col-6 md:col-4 mb-0"
        disabled={sementeCobertura}
      />
      <Form.InputNumber
        label="Distribuição de regulagem para semente:"
        name="distribuicaoRegulagemSemente"
        addonRight="m"
        containerClassName="field col-6 md:col-4 mb-0"
        disabled={sementeCobertura}
      />
      <Form.InputNumber
        label="Distribuição de regulagem para adubação:"
        name="distribuicaoRegulagemAdubo"
        addonRight="m"
        containerClassName="field col-6 md:col-4 mb-0"
        disabled={sementeCobertura}
      />
      <div className="field col-12 pb-3 md:pb-0 md:col-4 mb-0">
        <Form.Checkbox
          label="Cálculo com margem de segurança"
          name="comMargemSeguranca"
          containerClassName="mb-2 bold-font-size normal-font-size"
          disabled={sementeCobertura}
        />
        <p className="text-600 m-0">
          Se ativo, os valores em Regulagem, Regulagem Adubação e Qtd. uma
          variação de 3% para mais e para menos.
        </p>
      </div>
      <Form.InputTextarea
        name="observacoes"
        label="Observações:"
        containerClassName="col-12 bold-font-size"
        rows={5}
      />
    </div>
  );
};

Parametros.validationSchema = validationSchema.omit(['talhoes']);

export default Parametros;
